import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_EVENT_KEY_LABEL,
  NOTIFICATION_EVENT_KEYS,
  type NotificationChannel,
  type NotificationEventKey,
} from "@colab/shared";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ConnectionsPanel } from "@/features/api-tokens/connections-panel";
import { useNotificationPreferences } from "./use-notification-preferences";
import { useUpdateNotificationPreference } from "./use-update-notification-preference";

// Phase 7 sub-bucket 7 — partner-side settings. No legacy precedent; the
// scope is the notification-preferences grid + a link back to /portal/profile
// for personal info. Security (password change) is intentionally out of scope
// — partners reset via Supabase's hosted auth flow at /forgot-password.

const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  mail: "Email",
  chat: "Slack",
};

export function SettingsPage() {
  const query = useNotificationPreferences();
  const mutation = useUpdateNotificationPreference();
  const cells = query.data;

  // Map (event_key:channel) → enabled for O(1) lookup. Falls back to true
  // since the server pre-realizes the matrix; this is defensive against an
  // intermediate optimistic state.
  const lookup = new Map<string, boolean>();
  for (const cell of cells ?? []) {
    lookup.set(`${cell.event_key}:${cell.channel}`, cell.enabled);
  }

  return (
    <div className="max-w-3xl">
      <header className="mb-7">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage how Colab reaches you.
        </p>
      </header>

      <section className="mb-10">
        <div className="mb-4">
          <h2 className="text-lg font-semibold tracking-tight">Notifications</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Preferences will take effect with an upcoming delivery update.
          </p>
        </div>

        {query.isLoading ? (
          <div className="grid gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Event</th>
                  {NOTIFICATION_CHANNELS.map((channel) => (
                    <th key={channel} className="w-24 px-4 py-2 text-center font-medium">
                      {CHANNEL_LABEL[channel]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_EVENT_KEYS.map((event_key, idx) => (
                  <tr
                    key={event_key}
                    className={idx === 0 ? "" : "border-t"}
                  >
                    <td className="px-4 py-2.5 text-foreground">
                      {NOTIFICATION_EVENT_KEY_LABEL[event_key as NotificationEventKey]}
                    </td>
                    {NOTIFICATION_CHANNELS.map((channel) => {
                      const enabled =
                        lookup.get(`${event_key}:${channel}`) ?? true;
                      return (
                        <td key={channel} className="px-4 py-2.5 text-center">
                          <Switch
                            checked={enabled}
                            onCheckedChange={(next) =>
                              mutation.mutate({
                                event_key: event_key as NotificationEventKey,
                                channel: channel as NotificationChannel,
                                enabled: next,
                              })
                            }
                            aria-label={`${NOTIFICATION_EVENT_KEY_LABEL[event_key as NotificationEventKey]} via ${CHANNEL_LABEL[channel]}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-3 text-lg font-semibold tracking-tight">Personal info</h2>
        <Link
          to="/portal/profile"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          Edit profile
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </section>

      <ConnectionsPanel scope="portal" />
    </div>
  );
}
